/**
 * The Artifacts list (AI-734): everything this user has shared or been given access to, in the column the
 * conversation transcript otherwise occupies. Selecting a row opens it in the same artifact panel a card
 * does — so a shared artifact renders through exactly the machinery a thread's own artifact does, only
 * addressed by resource instead of by thread.
 */

import { useCallback, useMemo, useState } from "react"
import { Alert, Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { Download, GridViewOutlined } from "@mui/icons-material"
import { DataGridPremium } from "@mui/x-data-grid-premium"
import type { GridColDef, GridRenderCellParams, GridRowParams } from "@mui/x-data-grid-premium"
import { useUserId } from "resources/account"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import { useAnalytics } from "connected-components/analytics"
import type { FileArtifact } from "./artifact-contract"
import { type SharedArtifactRow, useMemberNames, useSharedArtifacts } from "./resource-sharing"
import { useArtifactDeepLink } from "./artifact-deep-link"
import { useArtifactPanelActions } from "./use-artifact-panel"
import { useArtifactDownload } from "./use-artifact-download"
import { MessageTime } from "./message-time"
import { ToolbarWithSearch } from "./toolbar-with-search"
import { FilterPill, HOVER_SURFACE, SURFACE_HEADER_SX, SURFACE_PILLS_SX, surfaceGridBaseSx, surfaceRootSx } from "./surface-styles"

const SURFACE_CONTAINER = "kelly-artifacts-surface"
const ROOT_SX = surfaceRootSx(SURFACE_CONTAINER)
// No column menus, no resize handles, no header separators: the grid is here for a sortable, searchable
// list of four fields, and every one of those affordances offers something this list cannot do.
const GRID_SX = ((theme: Theme) => ({
  ...surfaceGridBaseSx(theme, SURFACE_CONTAINER),
  "& .MuiDataGrid-columnSeparator": { display: "none" },
  // The whole row opens the artifact, so the whole row is the affordance: it takes the pointer and it is
  // what highlights. The one exception is the Download button, which stops the click.
  "& .MuiDataGrid-row": { cursor: "pointer" },
  "& .MuiDataGrid-row:hover": { backgroundColor: HOVER_SURFACE },
  // A mouse click otherwise leaves a focus ring around the single cell it landed on, which reads as "this
  // cell is selected" on a surface where cells mean nothing. :focus-visible still fires for keyboard
  // navigation, so the grid keeps its focus indicator where it is the only cue.
  "& .MuiDataGrid-cell:focus": { outline: "none" },
  // Centring for the two cells the grid does not lay out itself, as one rule on the parent rather than an
  // sx on every rendered cell. Scoped by field on purpose: the grid gives `.cell` text-overflow: ellipsis,
  // which a flex container would stop applying, and the Name cell depends on it.
  "& .MuiDataGrid-cell[data-field='sharedAt'], & .MuiDataGrid-cell[data-field='actions']": {
    display: "flex",
    alignItems: "center",
  },
})) satisfies SxProps<Theme>
const CENTER_SX = { display: "flex", justifyContent: "center", p: 3 } satisfies SxProps<Theme>
const EMPTY_SX = { display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 0.5, px: 3, py: 6 } satisfies SxProps<Theme>
const EMPTY_GLYPH_SX = { fontSize: "2.5rem", mb: 1, color: "text.disabled" } satisfies SxProps<Theme>
const EMPTY_BODY_SX = { color: "text.secondary", maxWidth: "22rem" } satisfies SxProps<Theme>

// The ids are the analytics values and the test handles — only the copy changes here.
const FILTERS = [
  { id: "all", label: "All" },
  { id: "yours", label: "Yours" },
  { id: "shared", label: "Shared with you" },
] as const

type ArtifactFilter = typeof FILTERS[number]["id"]

// Three different facts, so three headings — and each body says what would put something here.
const EMPTY_COPY: Record<ArtifactFilter, { heading: string, body: string }> = {
  all: { heading: "No artifacts yet", body: "Share a file from one of your conversations and it will show up here." },
  yours: { heading: "You haven't shared anything yet", body: "Open a file Kelly made and use Share to give a colleague access." },
  shared: { heading: "Nothing shared with you yet", body: "When a colleague shares an artifact with you or one of your teams, it shows up here." },
}

/**
 * The row's own download, which needs the artifact's identity — so it is a component rather than a
 * `renderCell` closure, and each row gets its own hook state instead of one shared spinner.
 */
function DownloadCell({ row }: { row: SharedArtifactRow }) {
  const artifact = useMemo<FileArtifact>(
    () => ({ id: row.sourceArtifactId, type: "file", filename: row.filename, contentType: row.contentType, resourceId: row.id }),
    [row],
  )
  const { downloadArtifact, isDownloading } = useArtifactDownload(artifact, null, row.id)

  return (
    <Tooltip title={`Download ${row.filename}`}>
      <span>
        <IconButton
          size="small"
          aria-label={`Download ${row.filename}`}
          disabled={isDownloading}
          // The row's own click opens the panel; a download is not an open.
          onClick={(event) => { event.stopPropagation(); downloadArtifact(null, row.sourceArtifactId) }}
        >
          <Download fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  )
}

function ArtifactsToolbar() {
  return <ToolbarWithSearch placeholder="Search artifacts…" ariaLabel="Search artifacts" />
}

export function ArtifactsSurface() {
  const userId = useUserId()
  const analytics = useAnalytics()
  const panel = useArtifactPanelActions()
  const [filter, setFilter] = useState<ArtifactFilter>("all")
  const selectFilter = useCallback((next: ArtifactFilter) => {
    if (next === filter) return
    analytics.track(AI_TEAMMATE_EVENTS.ARTIFACTS_FILTERED, { filter: next })
    setFilter(next)
  }, [analytics, filter])

  const { artifacts, isLoading: isLoadingArtifacts, isError } = useSharedArtifacts(true)
  const { names: memberNames, isLoading: isLoadingNames, isError: isNamesErrored } = useMemberNames(true)
  // A link to something this user cannot see says so here; the panel it would have opened never mounts.
  const deepLink = useArtifactDeepLink()
  // Both, because the rows and the names they are attributed to arrive on separate round trips. Showing
  // the list off the first one alone labels every colleague's share "—" for a beat, which reads as an
  // answer rather than as a gap.
  const isLoading = isLoadingArtifacts || isLoadingNames

  const rows = useMemo(() => {
    // `createdBy`, not the permission level: an admin reads OWNER on every resource in the account, so a
    // level-based split would file a colleague's share under "Yours" for them.
    if (filter === "yours") return artifacts.filter(artifact => artifact.createdBy === userId)
    if (filter === "shared") return artifacts.filter(artifact => artifact.createdBy !== userId)
    return artifacts
  }, [artifacts, filter, userId])

  // No opener is passed on: the grid row carries no tabIndex, so close()'s `opener.focus()` would
  // silently no-op and strand keyboard focus. Null takes the panel's safe fallback instead.
  const openArtifact = useCallback((row: SharedArtifactRow) => {
    if (panel == null) return
    // `resourceId` is what routes the panel's fetches at the resource endpoints; `id` stays the source
    // artifact id so every artifact analytics event keeps naming the same thing.
    panel.openArtifact(
      { id: row.sourceArtifactId, type: "file", filename: row.filename, contentType: row.contentType, resourceId: row.id },
      null,
      row.name,
    )
    analytics.track(AI_TEAMMATE_EVENTS.ARTIFACT_OPENED_FROM_LIST, {
      artifactId: row.sourceArtifactId,
      resourceId: row.id,
      filter,
      isOwn: row.createdBy === userId,
    })
  }, [panel, analytics, filter, userId])

  const columns = useMemo<GridColDef<SharedArtifactRow>[]>(() => {
    const sharedBy: GridColDef<SharedArtifactRow> = {
      field: "createdBy",
      headerName: "Shared by",
      flex: 0.6,
      minWidth: 120,
      // An em dash rather than "Someone else": a sharer missing from the directory has left the account,
      // and naming them "someone" claims we looked and found a person. That answer is only available
      // when the directory actually loaded — otherwise every colleague's share would claim it.
      valueGetter: (_value, row) => {
        if (row.createdBy === userId) return "You"
        return memberNames[row.createdBy] ?? (isNamesErrored ? "Unavailable" : "—")
      },
    }
    return [
      { field: "name", headerName: "Name", flex: 1, minWidth: 180 },
      // Under "Yours" the answer is always "You", so the column is noise there.
      ...(filter === "yours" ? [] : [sharedBy]),
      {
        field: "sharedAt",
        headerName: "Shared",
        flex: 0.5,
        minWidth: 110,
        renderCell: (params: GridRenderCellParams<SharedArtifactRow, string>) => <MessageTime date={params.value ?? ""} />,
      },
      {
        field: "actions",
        headerName: "",
        width: 56,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<SharedArtifactRow>) => <DownloadCell row={params.row} />,
      },
    ]
  }, [filter, memberNames, isNamesErrored, userId])

  return (
    <Box sx={ROOT_SX} data-testid="chatbox-artifacts-surface">
      <Box sx={SURFACE_HEADER_SX}>
        <Typography variant="h6" component="h2">Shared Artifacts</Typography>
        <Typography variant="caption" color="textDisabled">Open one to view it in the artifact panel</Typography>
      </Box>
      {deepLink.status === "not-found" && (
        <Alert severity="info" data-testid="chatbox-artifact-not-found">Artifact not found.</Alert>
      )}
      {isError && <Alert severity="error" data-testid="chatbox-artifacts-error">Couldn&apos;t load your artifacts. Please try again later.</Alert>}
      <Box sx={SURFACE_PILLS_SX} role="group" aria-label="Filter artifacts">
        {FILTERS.map(({ id, label }) => (
          <FilterPill
            key={id}
            label={label}
            size="small"
            variant="outlined"
            isSelected={filter === id}
            onClick={() => selectFilter(id)}
            aria-pressed={filter === id}
            data-testid={`chatbox-artifacts-filter-${id}`}
          />
        ))}
      </Box>

      {isLoading && !isError && (
        <Box sx={CENTER_SX} data-testid="chatbox-artifacts-loading">
          <CircularProgress size={20} aria-label="Loading artifacts" />
        </Box>
      )}
      {!isLoading && !isError && rows.length === 0 && (
        <Box sx={EMPTY_SX} data-testid="chatbox-artifacts-empty">
          <GridViewOutlined sx={EMPTY_GLYPH_SX} />
          <Typography variant="subtitle2">{EMPTY_COPY[filter].heading}</Typography>
          <Typography variant="body2" sx={EMPTY_BODY_SX}>{EMPTY_COPY[filter].body}</Typography>
        </Box>
      )}
      {!isLoading && !isError && rows.length > 0 && (
        <DataGridPremium
          rows={rows}
          columns={columns}
          density="compact"
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnResize
          hideFooter
          sx={GRID_SX}
          slots={{ toolbar: ArtifactsToolbar }}
          showToolbar
          initialState={{ sorting: { sortModel: [{ field: "sharedAt", sort: "desc" }] } }}
          onRowClick={(params: GridRowParams<SharedArtifactRow>) => openArtifact(params.row)}
          data-testid="chatbox-artifacts-grid"
        />
      )}
    </Box>
  )
}
